import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export const generateReceiptNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const timestamp = Date.now().toString().slice(-6);

  return `RCP${year}${month}${day}${timestamp}`;
};

export const generatePaymentReference = (): string => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const timestamp = Date.now().toString().slice(-6);

  return `PAY${year}${month}${day}${timestamp}`;
};

export const downloadReceiptAsPDF = async (
  receiptElement: HTMLElement,
  studentName: string,
  receiptNumber: string
): Promise<void> => {
  try {
    // Create a temporary style element to override problematic CSS
    const styleOverride = document.createElement("style");
    styleOverride.textContent = `
      /* Override any oklch color functions with fallback colors */
      * {
        color: inherit !important;
      }
      .text-gray-800 { color: #1f2937 !important; }
      .text-gray-600 { color: #4b5563 !important; }
      .text-gray-500 { color: #6b7280 !important; }
      .text-gray-400 { color: #9ca3af !important; }
      .text-green-600 { color: #059669 !important; }
      .text-red-600 { color: #dc2626 !important; }
      .bg-white { background-color: #ffffff !important; }
      .border-gray-300 { border-color: #d1d5db !important; }
      .border-gray-200 { border-color: #e5e7eb !important; }
    `;
    document.head.appendChild(styleOverride);

    // Clone the element to avoid issues with the original DOM
    const clonedElement = receiptElement.cloneNode(true) as HTMLElement;

    // Force specific dimensions on the cloned element to prevent it from expanding
    clonedElement.style.width = receiptElement.offsetWidth + "px";
    clonedElement.style.height = receiptElement.offsetHeight + "px";
    clonedElement.style.maxWidth = receiptElement.offsetWidth + "px";
    clonedElement.style.maxHeight = receiptElement.offsetHeight + "px";

    // Create a temporary container for the cloned element
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.left = "-9999px";
    tempContainer.style.top = "-9999px";
    tempContainer.style.width = receiptElement.offsetWidth + "px";
    tempContainer.style.height = receiptElement.offsetHeight + "px";
    tempContainer.style.overflow = "hidden"; // Prevent any overflow
    tempContainer.appendChild(clonedElement);
    document.body.appendChild(tempContainer);

    // Wait for the cloned element to be fully rendered
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Create canvas from the cloned element with reduced scale for smaller file size
    const canvas = await html2canvas(clonedElement, {
      backgroundColor: "#ffffff",
      scale: 1.2, // Reduced scale for smaller file size while maintaining quality
      useCORS: true,
      allowTaint: true,
      ignoreElements: (element) => {
        // Skip elements that might cause issues or duplication
        return (
          element.classList.contains("print:hidden") ||
          element.classList.contains("fixed") ||
          element.classList.contains("inset-0") ||
          element.classList.contains("bg-black/50") ||
          element.classList.contains("z-50") ||
          element.tagName === "BUTTON" ||
          element.closest(".fixed") !== null
        );
      },
      logging: false, // Disable logging to avoid console spam
    });

    // Remove the temporary container
    document.body.removeChild(tempContainer);

    // Apply CSS overrides after canvas creation
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // The canvas is already created, so we don't need the onclone callback
      // The CSS overrides from the style element should be sufficient
    }

    // Remove the temporary style override
    document.head.removeChild(styleOverride);

    // Calculate dimensions for single page
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Create PDF with optimized settings
    const pdf = new jsPDF("p", "mm", "a4");

    // Add the image to PDF - fit to page width, maintain aspect ratio
    const imageData = canvas.toDataURL("image/jpeg", 0.8); // Use JPEG with 80% quality for smaller file size

    // If content is taller than page, scale it down to fit
    let finalImgHeight = imgHeight;
    let finalImgWidth = imgWidth;

    if (imgHeight > pageHeight) {
      // Scale down to fit page height
      const scaleFactor = pageHeight / imgHeight;
      finalImgHeight = pageHeight;
      finalImgWidth = imgWidth * scaleFactor;
    }

    // Center horizontally if scaled
    const xOffset = (imgWidth - finalImgWidth) / 2;

    // Use lower quality for smaller file size
    pdf.addImage(
      imageData,
      "JPEG",
      xOffset,
      0,
      finalImgWidth,
      finalImgHeight,
      undefined,
      "FAST"
    );

    // Download the PDF
    const fileName = `Receipt_${receiptNumber}_${studentName.replace(
      /\s+/g,
      "_"
    )}.pdf`;

    pdf.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF receipt");
  }
};

export const printReceipt = (receiptElement: HTMLElement): void => {
  // Create a new window for printing
  const printWindow = window.open("", "_blank", "width=800,height=600");

  if (!printWindow) {
    throw new Error(
      "Unable to open print window. Please check if popups are blocked."
    );
  }

  // Get the receipt HTML content
  const receiptHTML = receiptElement.outerHTML;

  // Create print document
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payment Receipt</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          background: white;
        }
        .print\\:hidden {
          display: none !important;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
        @page {
          margin: 0.5in;
        }
      </style>
    </head>
    <body>
      ${receiptHTML}
    </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    // Don't close automatically to allow user to cancel print
    setTimeout(() => {
      printWindow.close();
    }, 1000);
  };
};
